const LEDGER_FIELDS = ['gold', 'gems', 'marketScore', 'discoveries'];
const STANDARD_SYNC_RETRY_MS = 15_000;
const MAX_INVENTORY_QUANTITY = 999999;
const MAX_GEMS = 1000000000;
const MAX_DISCOVERIES = 8;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function safeInteger(value, maximum = Number.MAX_SAFE_INTEGER) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, Math.min(maximum, Math.floor(number)));
}

function integerDelta(value, maximum = Number.MAX_SAFE_INTEGER) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(-maximum, Math.min(maximum, Math.trunc(number)));
}

function inventoryObject(value) {
  if (Array.isArray(value)) {
    return Object.fromEntries(value
      .map(entry => [String(entry?.itemId || ''), safeInteger(entry?.quantity, MAX_INVENTORY_QUANTITY)])
      .filter(([itemId, quantity]) => itemId && quantity > 0));
  }
  if (!value || typeof value !== 'object') return {};
  return Object.fromEntries(Object.entries(value)
    .map(([itemId, quantity]) => [itemId, safeInteger(quantity, MAX_INVENTORY_QUANTITY)])
    .filter(([itemId, quantity]) => itemId && quantity > 0));
}

export function ledgerSnapshot(value) {
  return {
    gold: safeInteger(value?.gold),
    gems: safeInteger(value?.gems, MAX_GEMS),
    marketScore: safeInteger(value?.marketScore ?? value?.market_score),
    discoveries: Array.isArray(value?.discoveries)
      ? Math.min(MAX_DISCOVERIES, new Set(value.discoveries).size)
      : safeInteger(value?.discoveries, MAX_DISCOVERIES),
    inventory: inventoryObject(value?.inventory),
  };
}

export function ledgerDelta(targetValue, baseValue) {
  const target = ledgerSnapshot(targetValue);
  const base = ledgerSnapshot(baseValue);
  const itemIds = new Set([...Object.keys(target.inventory), ...Object.keys(base.inventory)]);
  return {
    gold: target.gold - base.gold,
    gems: target.gems - base.gems,
    marketScore: target.marketScore - base.marketScore,
    discoveries: Math.max(0, target.discoveries - base.discoveries),
    inventory: Object.fromEntries([...itemIds]
      .map(itemId => [itemId, (target.inventory[itemId] || 0) - (base.inventory[itemId] || 0)])
      .filter(([, quantity]) => quantity !== 0)),
  };
}

export function hasLedgerDelta(delta) {
  return LEDGER_FIELDS.some(field => Number(delta?.[field]) !== 0)
    || Object.values(delta?.inventory || {}).some(value => Number(value) !== 0);
}

export function applyLedgerDelta(baseValue, deltaValue) {
  const base = ledgerSnapshot(baseValue);
  const inventory = { ...base.inventory };
  for (const [itemId, change] of Object.entries(deltaValue?.inventory || {})) {
    const quantity = safeInteger(
      (inventory[itemId] || 0) + integerDelta(change, MAX_INVENTORY_QUANTITY),
      MAX_INVENTORY_QUANTITY,
    );
    if (quantity) inventory[itemId] = quantity;
    else delete inventory[itemId];
  }
  return {
    gold: safeInteger(base.gold + integerDelta(deltaValue?.gold)),
    gems: safeInteger(base.gems + integerDelta(deltaValue?.gems, MAX_GEMS), MAX_GEMS),
    marketScore: safeInteger(base.marketScore + integerDelta(deltaValue?.marketScore)),
    discoveries: safeInteger(
      base.discoveries + integerDelta(deltaValue?.discoveries, MAX_DISCOVERIES),
      MAX_DISCOVERIES,
    ),
    inventory,
  };
}

export function applyLedgerSnapshot(state, snapshotValue) {
  const snapshot = ledgerSnapshot(snapshotValue);
  return {
    ...state,
    gold: snapshot.gold,
    gems: snapshot.gems,
    marketScore: snapshot.marketScore,
    inventory: Object.entries(snapshot.inventory).map(([itemId, quantity]) => ({ itemId, quantity })),
  };
}

export function normalizeCloudSync(value, userId) {
  if (!value || value.userId !== userId || !value.baseline) return null;
  const normalized = {
    userId,
    baseline: ledgerSnapshot(value.baseline),
    pending: null,
  };
  if (
    typeof value.pending?.eventId === 'string'
    && UUID_PATTERN.test(value.pending.eventId)
    && value.pending?.target
    && value.pending?.delta
  ) {
    normalized.pending = {
      eventId: String(value.pending.eventId),
      target: ledgerSnapshot(value.pending.target),
      delta: {
        gold: integerDelta(value.pending.delta.gold),
        gems: integerDelta(value.pending.delta.gems, MAX_GEMS),
        marketScore: integerDelta(value.pending.delta.marketScore),
        discoveries: integerDelta(value.pending.delta.discoveries, MAX_DISCOVERIES),
        inventory: Object.fromEntries(Object.entries(value.pending.delta.inventory || {})
          .map(([itemId, quantity]) => [itemId, integerDelta(quantity, MAX_INVENTORY_QUANTITY)])
          .filter(([itemId, quantity]) => itemId && quantity !== 0)),
      },
    };
  }
  return normalized;
}

export function prepareCloudSync(state, userId, eventId) {
  const cloudSync = normalizeCloudSync(state.cloudSync, userId);
  if (!cloudSync) return null;
  if (cloudSync.pending) return { cloudSync, pending: cloudSync.pending };
  const target = ledgerSnapshot(state);
  const delta = ledgerDelta(target, cloudSync.baseline);
  if (!hasLedgerDelta(delta)) return { cloudSync, pending: null };
  const pending = { eventId, target, delta };
  return {
    cloudSync: { ...cloudSync, pending },
    pending,
  };
}

export function mergeCloudSyncReceipt(state, userId, receiptValue) {
  const cloudSync = normalizeCloudSync(state.cloudSync, userId);
  if (!cloudSync?.pending) return state;
  const server = ledgerSnapshot(receiptValue);
  const residual = ledgerDelta(ledgerSnapshot(state), cloudSync.pending.target);
  const merged = applyLedgerDelta(server, residual);
  return {
    ...applyLedgerSnapshot(state, merged),
    cloudSync: {
      userId,
      baseline: server,
      pending: null,
    },
  };
}

export function chooseCloudLedger(state, userId, serverValue, choice) {
  const server = ledgerSnapshot(serverValue);
  if (choice === 'server') {
    return {
      ...applyLedgerSnapshot(state, server),
      cloudSync: { userId, baseline: server, pending: null },
    };
  }
  return {
    ...state,
    cloudSync: { userId, baseline: server, pending: null },
  };
}

export function cloudSyncRetryDelay(error, now = Date.now()) {
  const message = String(error || '').toLowerCase();
  if (!/reconciliation.*(?:budget|limit reached)/.test(message)) {
    return STANDARD_SYNC_RETRY_MS;
  }
  const current = new Date(now);
  const nextUtcDay = Date.UTC(
    current.getUTCFullYear(),
    current.getUTCMonth(),
    current.getUTCDate() + 1,
  );
  return Math.max(60_000, nextUtcDay - now + 5000);
}
